import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Copy, Download, Link2, QrCode, Megaphone, Mail, Bell, ListChecks, ArrowRight, ArrowLeft, Clock,
} from "lucide-react";
import { api } from "@/lib/api";
import { FadeIn } from "@/components/common";
import { ProducerShell, ProducerNav, PSectionLabel, PSpinner } from "@/components/producer";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

function CopyBlock({ label, icon: Icon, text, testId }) {
  const copy = () => { navigator.clipboard.writeText(text); toast.success(`${label} copied`); };
  return (
    <div className="p-card p-5" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 p-accent-text" />
          <p className="text-sm font-semibold">{label}</p>
        </div>
        <button data-testid={`${testId}-copy`} onClick={copy}
          className="p-btn-ghost flex items-center gap-1.5 px-3 py-1.5 text-xs">
          <Copy className="h-3.5 w-3.5" /> Copy
        </button>
      </div>
      <pre className="mt-3 max-h-48 overflow-y-auto whitespace-pre-wrap rounded-lg p-inset p-3 text-xs p-muted">{text}</pre>
    </div>
  );
}

export default function EventSetup() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["setup", eventId],
    queryFn: async () => (await api.get(`/producer/events/${eventId}/setup`)).data,
  });

  const toggle = async (key, done) => {
    try {
      await api.post(`/producer/events/${eventId}/setup/checklist`, { key, done });
      qc.invalidateQueries({ queryKey: ["setup", eventId] });
    } catch { toast.error("Could not update step"); }
  };

  const finish = async () => {
    try {
      await api.post(`/producer/events/${eventId}/setup/finish`);
      qc.invalidateQueries({ queryKey: ["producer-events"] });
      toast.success("Setup complete");
      navigate(`/producer/events/${eventId}`);
    } catch { toast.error("Could not finish setup"); }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/producer/events/${eventId}/entry-kit.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `goose-${data.event.goose_id}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  if (isLoading) return <ProducerShell><ProducerNav active="events" /><PSpinner /></ProducerShell>;
  if (error) return (
    <ProducerShell><ProducerNav active="events" />
      <div className="mx-auto max-w-md px-6 py-20 text-center">
        <h2 className="font-display text-2xl font-bold">Event not found</h2>
        <Button onClick={() => navigate("/producer")} className="p-btn mt-6">Back to events</Button>
      </div>
    </ProducerShell>
  );

  const { event: ev, assets, setup } = data;
  const steps = setup.steps || [];
  const doneCount = steps.filter((s) => s.done).length;

  return (
    <ProducerShell>
      <ProducerNav active="events" />
      <div className="mx-auto max-w-6xl px-6 py-8">
        <FadeIn>
          <button data-testid="setup-back-to-events" onClick={() => navigate("/producer")} className="flex items-center gap-1.5 text-sm p-muted hover:opacity-80">
            <ArrowLeft className="h-4 w-4" /> All events
          </button>
          <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
            <div>
              <PSectionLabel>Event setup · add Goose to your event</PSectionLabel>
              <h1 className="font-display mt-2 text-3xl font-bold sm:text-4xl">{ev.name}</h1>
              <p className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm p-muted">
                <span className="font-mono-g p-subtle">{ev.goose_id}</span>
                <span>{ev.date} · {ev.start_time} · {ev.timezone}</span>
                <span>{ev.location}</span>
              </p>
              <p className="mt-1 flex items-center gap-2 text-xs text-amber-600">
                <Clock className="h-3.5 w-3.5" /> {assets.cutoff_label}: {assets.cutoff_text}
              </p>
              <p className="mt-1 max-w-2xl text-xs p-subtle" data-testid="cutoff-note">{assets.cutoff_note}</p>
            </div>
            <Button data-testid="setup-go-dashboard" onClick={() => navigate(`/producer/events/${eventId}`)}
              className="p-btn-ghost">
              Go to dashboard <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </div>
        </FadeIn>

        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-12">
          {/* checklist */}
          <div className="lg:col-span-4">
            <FadeIn>
              <div className="p-card p-6" data-testid="setup-checklist">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2"><ListChecks className="h-4 w-4 p-accent-text" /><p className="text-sm font-semibold">Setup checklist</p></div>
                  <span className="font-mono-g text-xs p-muted">{doneCount}/{steps.length}</span>
                </div>
                <div className="mt-4 space-y-3">
                  {steps.map((s) => (
                    <label key={s.key} data-testid={`step-${s.key}`} className="flex cursor-pointer items-start gap-3 rounded-xl p-inset p-3">
                      <Checkbox data-testid={`step-${s.key}-checkbox`} className="mt-0.5" disabled={s.kind === "system"}
                        checked={!!s.done} onCheckedChange={(v) => s.kind !== "system" && toggle(s.key, !!v)} />
                      <div>
                        <p className={`flex items-center gap-2 text-sm font-medium ${s.done ? "p-subtle line-through" : ""}`}>
                          {s.title}
                          {s.kind === "system" && <span className="rounded-full bg-sky-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-sky-600 no-underline">Auto</span>}
                        </p>
                        <p className="text-xs p-subtle">{s.desc}</p>
                      </div>
                    </label>
                  ))}
                </div>
                <Button data-testid="setup-finish-btn" onClick={finish} disabled={doneCount < steps.length}
                  className="p-btn mt-5 w-full">
                  {setup.complete ? "Setup complete — open dashboard" : "Finish setup"}
                </Button>
                {!setup.complete && doneCount < steps.length && (
                  <p className="mt-2 text-center text-xs p-subtle">Check off the manual steps to finish. You can use the dashboard any time.</p>
                )}
              </div>
            </FadeIn>

            <FadeIn delay={0.08}>
              <div className="p-card mt-6 p-6" data-testid="setup-link-qr">
                <div className="flex items-center gap-2"><Link2 className="h-4 w-4 p-accent-text" /><p className="text-sm font-semibold">Attribution link & QR</p></div>
                <button data-testid="setup-copy-link" onClick={() => { navigator.clipboard.writeText(assets.attribution_url); toast.success("Attribution link copied"); }}
                  className="mt-3 flex w-full items-center justify-between gap-2 rounded-lg p-inset p-hover-accent p-2.5 text-left text-xs p-muted">
                  <span className="truncate">{assets.attribution_url}</span><Copy className="h-3.5 w-3.5 shrink-0" />
                </button>
                <div className="mt-4 flex items-center gap-4">
                  <img src={assets.qr_data_url} alt="Event QR" className="h-28 w-28 rounded-xl border p-border-c bg-white p-2" data-testid="setup-qr" />
                  <Button data-testid="setup-download-pdf" onClick={downloadPdf} className="p-btn-ghost">
                    <Download className="mr-1.5 h-4 w-4" /> Printable kit
                  </Button>
                </div>
              </div>
            </FadeIn>
          </div>

          {/* instructions + copy */}
          <div className="space-y-6 lg:col-span-8">
            <FadeIn>
              <div className="p-card p-6" data-testid="eventbrite-instructions">
                <div className="flex items-center gap-2"><QrCode className="h-4 w-4 p-accent-text" /><p className="text-sm font-semibold">Add Goose to your Eventbrite event</p></div>
                <p className="mt-2 text-xs p-subtle">Goose doesn't integrate with Eventbrite in V1 — these are steps for you to follow in your Eventbrite dashboard.</p>
                <ol className="mt-4 space-y-2">
                  {assets.eventbrite_instructions.map((step, i) => (
                    <li key={i} className="flex gap-3 text-sm p-muted">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold p-accent-text" style={{ background: "var(--p-accent-soft)" }}>{i + 1}</span>
                      <span>{step}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </FadeIn>
            <FadeIn delay={0.06}><CopyBlock testId="copy-guest" label="Guest announcement (event description)" icon={Megaphone} text={assets.copy.guest_announcement} /></FadeIn>
            <FadeIn delay={0.1}><CopyBlock testId="copy-confirmation" label="Eventbrite confirmation email" icon={Mail} text={assets.copy.confirmation_email} /></FadeIn>
            <FadeIn delay={0.14}><CopyBlock testId="copy-reminder" label="Reminder message" icon={Bell} text={assets.copy.reminder_message} /></FadeIn>
          </div>
        </div>
      </div>
    </ProducerShell>
  );
}
